import { Tag } from 'antd';
import { FC, ReactElement } from 'react';
import { useQueryClient } from 'react-query';

import { AppMessage, AppModal } from '@/common/components';
import { AppModalControlProps } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import {
  PagesQueryKeys,
  useSearchAndReplaceHistoryQuery,
  useSearchAndReplaceHistoryRollbackMutation,
} from '@/modules/Pages';

import { PagesSearchAndReplaceHistoryModalView } from './PagesSearchAndReplaceHistoryModalView';

interface Props {
  siteId: string;
  controls: AppModalControlProps;
}

export const PagesSearchAndReplaceHistoryModalContainer: FC<Props> = ({ siteId, controls }) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const {
    data: historyData,
    error: historyError,
    isLoading: isHistoryLoading,
    isFetching: isHistoryFetching,
    isError: isHistoryError,
  } = useSearchAndReplaceHistoryQuery(siteId)({ enabled: controls.isOpen });

  const { mutate: searchAndReplaceHistoryRollbackMutation, isLoading: searchAndReplaceHistoryRollbackIsLoading } =
    useSearchAndReplaceHistoryRollbackMutation(siteId)({
      onSuccess: async () => {
        appMessageApi.success('Запит на поверення даних до попереднього стану відправлено успішно!');
        await queryClient.invalidateQueries([PagesQueryKeys.GetAll], { exact: false });
        await queryClient.invalidateQueries([PagesQueryKeys.SearchAndReplaceHistory, siteId]);
        controls.close();
      },
      onError: (error) => {
        messageOnError(error, appMessageApi);
      },
    });

  const handleRollback = (version: string) => {
    searchAndReplaceHistoryRollbackMutation({ version });
  };

  return (
    <AppModal
      title={
        <>
          Історія пошуку та заміни <Tag color="blue">{historyData?.length ?? 0}</Tag>
        </>
      }
      width={500}
      footer={false}
      maskClosable={false}
      closable={!searchAndReplaceHistoryRollbackIsLoading}
      controls={controls}
    >
      <FallbackedLayout
        isLoading={isHistoryLoading || isHistoryFetching}
        isError={isHistoryError}
        error={historyError}
        data={historyData}
      >
        {(history): ReactElement => (
          <PagesSearchAndReplaceHistoryModalView
            history={history}
            loading={searchAndReplaceHistoryRollbackIsLoading}
            handleRollback={handleRollback}
          />
        )}
      </FallbackedLayout>
    </AppModal>
  );
};
